import * as React from 'react';

import cx from 'classnames';

import { FocusIndicator } from '~shared/components/FocusIndicator';

type InputElementProps = React.AllHTMLAttributes<HTMLInputElement>;
export type CheckboxChecked =
  | NonNullable<InputElementProps['checked']>
  | 'mixed';

export interface Props {
  id?: NonNullable<InputElementProps['id']>;
  size?: 'large' | 'medium';
  onChange: NonNullable<InputElementProps['onChange']>;
  value?: InputElementProps['value'];
  name?: InputElementProps['name'];
  'aria-describedby'?: InputElementProps['aria-describedby'];
  'aria-labelledby'?: InputElementProps['aria-labelledby'];
  'aria-label'?: InputElementProps['aria-label'];
  disabled?: InputElementProps['disabled'];
  checked: CheckboxChecked;
  /** Define the colour applied when checked. */
  appearance?: 'default' | 'positive';
}

/**
 * Custom checkbox input which can support standard two-state or tri-state
 * operations. The `disabled` prop applies a "soft disable" using
 * `aria-disabled`; be sure to account for this in the change handler, as
 * clicks, etc. will still fire on disabled inputs.
 *
 * @see https://www.w3.org/TR/wai-aria-practices/#checkbox
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#attr-indeterminate
 */
export const Checkbox = React.forwardRef<HTMLInputElement, Props>(
  function Checkbox(
    {
      appearance = 'default',
      size = 'medium',
      checked,
      disabled,
      onChange,
      ...restProps
    },
    forwardedRef
  ) {
    const isMixed = checked === 'mixed';
    const isChecked = checked === true;
    const isMixedOrChecked = isMixed || isChecked;
    const defaultRef = React.useRef<HTMLInputElement>(null);
    const inputRef = forwardedRef || defaultRef;
    const indeterminateRef = React.useRef(false);
    const [hasFocus, setHasFocus] = React.useState(false);

    React.useEffect(() => {
      if (inputRef && typeof inputRef === 'object' && inputRef.current) {
        inputRef.current.indeterminate = isMixed;
        indeterminateRef.current = isMixed;
      }
    }, [inputRef, isMixed]);

    const handleChangeFocus = () => {
      setHasFocus(!hasFocus);
    };

    const handleMixedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (inputRef && typeof inputRef === 'object' && inputRef.current) {
        inputRef.current.indeterminate = indeterminateRef.current;
      }
      if (typeof onChange === 'function') {
        onChange(e);
      }
    };

    const iconClass = cx('group flex-grow-0 flex-shrink-0 checkbox-icon', {
      'opacity-50': disabled,
    });
    const circleClass = cx(
      'checkbox-icon__circle',
      !isMixedOrChecked && [
        hasFocus ? 'stroke-cyan-600' : 'stroke-grey-800',
        !disabled && 'group-hover:stroke-cyan-600',
      ],
      isMixedOrChecked && [
        appearance === 'default' && [
          'stroke-cyan-500 fill-cyan-500',
          !disabled && 'group-hover:stroke-cyan-600 group-hover:fill-cyan-600',
        ],
        appearance === 'positive' && [
          'stroke-green-500 fill-green-500',
          !disabled &&
            'group-hover:stroke-green-600 group-hover:fill-green-600',
        ],
      ]
    );
    const dashClass = cx(isMixed ? 'fill-white' : 'fill-transparent');
    const checkClass = cx(
      'checkbox-icon__checkmark',
      !isMixedOrChecked && [
        hasFocus ? 'stroke-cyan-600' : 'stroke-transparent',
        !disabled && 'group-hover:stroke-cyan-600',
      ],
      isChecked ? 'stroke-white' : 'stroke-transparent'
    );

    return (
      <div
        className="group relative w-8 h-8 rounded-pill"
        style={{
          transform: size === 'medium' ? 'scale(0.83333333334)' : 'scale(1)',
        }}
      >
        {isMixedOrChecked ? <FocusIndicator /> : null}
        <input
          ref={inputRef}
          className={`absolute w-full h-full m-0 appearance-none opacity-0 ${
            !disabled && 'cursor-pointer'
          }`}
          type="checkbox"
          checked={isMixed ? false : (checked as boolean)}
          aria-checked={checked}
          aria-disabled={disabled}
          onChange={isMixed ? handleMixedChange : onChange}
          onFocus={handleChangeFocus}
          onBlur={handleChangeFocus}
          {...restProps}
        />
        <svg
          className={iconClass}
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className={circleClass}
            cx="12"
            cy="12"
            r="11"
            strokeWidth="2"
          />
          <rect
            className={dashClass}
            width="14"
            height="2"
            x="5"
            y="11"
            rx="1"
          />
          <path
            className={checkClass}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M6.5 12.5L10 16l7-7"
          />
        </svg>
      </div>
    );
  }
);
